import { useCallback, useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Globe, Plus, Settings2, Loader2, Eye, EyeOff } from "lucide-react";
import { AudiencePicker } from "@/components/AudiencePicker";
import { ModuleSettingsDialog } from "@/components/ModuleSettingsDialog";
import { SiteCard } from "@/components/websites/SiteCard";
import { SCHEDULE_LABEL, CTRL } from "@/components/websites/util";
import { useDash } from "./ctx";
import { V } from "@/lib/vocab";

// Website Knowledge — websites the concierge learns from. One card per site; expand for pages / documents / FAQs / review / runs.
export default function Websites() {
  const { tenantId, tenant, role, actualRole } = useDash();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(false);
  // "Who can add websites" is a company-level permission, so it follows the person's REAL seat —
  // a company admin drilled into a community is shown as hoa_manager (effRole) and used to lose it.
  const isAdmin = ["company_admin", "super_admin", "platform_admin", "onboarding_admin"].includes(actualRole || role);
  const load = useCallback(() => api.get(`/tenants/${tenantId}/websites`).then((r) => setData(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load websites")), [tenantId]);
  useEffect(() => { if (tenantId) load(); }, [tenantId, load]);
  useEffect(() => {
    if (!data?.sites?.some((s) => ["queued", "crawling"].includes(s.status))) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [data, load]);

  return (
    <div className="space-y-6" data-testid="websites-page">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <PageTitle title={<><Globe className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Website Knowledge</>} about="Add a website and the concierge reads its pages, pulls in every document they link to (PDFs, Word, Excel — on any host, including TownSq file links) and turns FAQs into quick facts. Each run is a diff: new, changed and removed items are tracked and only changes are re-indexed. Official documents and facts always win over website content." />
        </div>
        {isAdmin && <button type="button" data-testid="websites-settings-button" onClick={() => setCfg(true)} className="h-10 w-10 rounded-full border border-sand-200 bg-white flex items-center justify-center hover:border-ink transition-colors shrink-0" title="Who can add websites"><Settings2 className="h-4 w-4" /></button>}
      </div>

      {data?.can_add && <AddSiteForm tenantId={tenantId} schedules={data.schedules} onAdded={load} />}

      {!data ? <div className="text-sm text-muted-foreground p-6">Loading…</div>
        : !data.sites.length ? (
          <div data-testid="websites-empty" className="bg-white rounded-2xl border border-dashed border-sand-300 p-10 text-center text-muted-foreground">
            <Globe className="h-8 w-8 mx-auto text-ink/20 mb-2" />
            <div className="font-semibold text-ink">No websites yet</div>
            <div className="text-sm mt-1 max-w-md mx-auto">Paste your {V.lc("org")}'s website above. Pages, linked documents and FAQs become answers within a few minutes.</div>
          </div>
        ) : data.sites.map((site) => <SiteCard key={site.id} site={site} tenantId={tenantId} canAdd={data.can_add} onChanged={load} refreshesLeft={data.manual_refreshes_left}
          schedules={data.schedules} initiallyOpen={params.get("site") === site.id} />)}

      {data && data.manual_refreshes_left != null && data.manual_refreshes_left <= 3 && data.sites.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="websites-refresh-budget">
          {data.manual_refreshes_left === 0
            ? `This ${V.lc("org")} has used its ${data.manual_refresh_daily_cap} manual refreshes for today — scheduled refreshes still run; the button unlocks tomorrow.`
            : `${data.manual_refreshes_left} of ${data.manual_refresh_daily_cap} manual refreshes left today. Each site can be refreshed once every ${data.manual_refresh_cooldown_min} minutes.`}
        </p>
      )}

      <ModuleSettingsDialog module={cfg ? "websites" : null} tenant={tenant} tenantId={tenantId} onClose={() => { setCfg(false); load(); }} onSaved={load} />
    </div>
  );
}

// New websites start on the product defaults — there is no community-wide default any more (iter507);
// every one of these is editable in that website's own Settings the moment it's added.
function AddSiteForm({ tenantId, schedules = [], onAdded }) {
  const [url, setUrl] = useState("");
  const [aud, setAud] = useState(["anyone"]);
  const [monitoring, setMonitoring] = useState(true);
  const [schedule, setSchedule] = useState("weekly");
  const [publish, setPublish] = useState("auto");
  const [adding, setAdding] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setAdding(true);
    try {
      await api.post(`/tenants/${tenantId}/websites`, { url: url.trim(), audiences: aud, monitoring_on: monitoring, schedule, publish_mode: publish });
      toast.success("Reading the site — pages and documents appear as they're found");
      setUrl(""); setAud(["anyone"]); onAdded();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't add that website"); }
    finally { setAdding(false); }
  };
  return (
    <form onSubmit={add} className="bg-white rounded-2xl border border-sand-200 p-4 space-y-3" data-testid="websites-add-form">
      <div className="flex flex-col sm:flex-row gap-2">
        <input data-testid="websites-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.yourcommunity.com" inputMode="url"
          className="flex-1 rounded-xl border border-sand-200 bg-sand-50/60 px-4 py-2.5 text-sm outline-none focus:bg-white focus:border-ink" />
        <button type="submit" disabled={adding || !url.trim()} data-testid="websites-add-button" className="inline-flex items-center justify-center gap-1.5 rounded-full bg-redline-500 text-white px-5 py-2.5 text-sm font-bold hover:bg-redline-600 disabled:opacity-50">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add website
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3 text-xs text-ink/70">
        <div className="min-w-0 sm:col-span-2 lg:col-span-1">
          <div className="font-semibold mb-1.5" title="The starting point for everything this site produces; you can set any page, document or FAQ individually afterwards.">Default — who can the AI share it with?</div>
          <AudiencePicker tenantId={tenantId} testid="websites-audience" compact audiences={aud} onAudiences={setAud} />
        </div>
        <label className="min-w-0 flex flex-col gap-1.5"><span className="font-semibold">Monitoring</span>
          <select data-testid="websites-add-monitoring" value={monitoring ? "on" : "off"} onChange={(e) => setMonitoring(e.target.value === "on")} className={`${CTRL} w-full`}><option value="on">On</option><option value="off">Off — refresh by hand</option></select></label>
        {monitoring && <label className="min-w-0 flex flex-col gap-1.5"><span className="font-semibold">Check</span>
          <select data-testid="websites-add-schedule" value={schedule} onChange={(e) => setSchedule(e.target.value)} className={`${CTRL} w-full`}>{schedules.map((k) => <option key={k} value={k}>{SCHEDULE_LABEL[k] || k}</option>)}</select></label>}
        <label className="min-w-0 flex flex-col gap-1.5"><span className="font-semibold">New content</span>
          <select data-testid="websites-add-publish" value={publish} onChange={(e) => setPublish(e.target.value)} className={`${CTRL} w-full`}><option value="auto">Publish automatically</option><option value="review">Hold for my review</option></select></label>
      </div>
      <p className="text-xs text-muted-foreground inline-flex items-start gap-1.5">{publish === "review" ? <EyeOff className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <Eye className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
        <span>The first import always publishes everything so you can see what was found. {publish === "review" ? "After that, new and changed items wait in Review until you approve them." : "After that, changes go live as soon as they're found."} It starts at 150 pages, 50 MB per file and 500 MB per run — every one of those is yours to change in this website's own Settings once it's added. Never logs in, never fills forms, honours robots rules.</span></p>
    </form>
  );
}
